import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceCrossNav from '../components/ServiceCrossNav';
import ricsValuer from '../assets/RICS_RegisteredValuer_Accreditation.png';
import './ServicePage.css';

const Expertise = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Expertise & Évaluation Immobilière au Maroc | ATAA REALTY (RICS, IVS)"
        description="Expertise et évaluation immobilière au Maroc conformes aux standards RICS et IVS : analyse juridique, technique, économique, méthodes d'évaluation et rapports d'expertise traçables. ATAA REALTY, Casablanca."
        canonical="https://ataarealty.com/services/expertise"
        keywords="expertise immobilière Maroc, évaluation immobilière, expert immobilier RICS, expert MRICS Casablanca, valuation immobilière, IVS, IFRS, rapport d'expertise immobilière"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Expertise / Évaluation Immobilière',
          provider: { '@type': 'ProfessionalService', name: 'ATAA REALTY', url: 'https://ataarealty.com/' },
          areaServed: { '@type': 'Country', name: 'Maroc' },
          description: "Acte d'analyse professionnelle structuré pour produire une appréciation fondée de la valeur d'un actif immobilier, conduit en alignement avec les standards RICS et IVS.",
          serviceType: 'Expertise immobilière, évaluation immobilière',
          url: 'https://ataarealty.com/services/expertise'
        }}
      />
      <Navbar />

      {/* HERO */}
      <header className="svc-header svc-bg-expertise">
        <div className="svc-overlay"></div>
        <div className="container svc-title-box svc-anim-up">
          <span className="svc-eyebrow">Notre Savoir-Faire</span>
          <h1>Expertise / Évaluation Immobilière</h1>
        </div>
      </header>

      {/* INTRO + APPROACH */}
      <section className="svc-content">
        <div className="container">
          <div className="svc-section svc-anim-up svc-delay-1">
            <img
              src={ricsValuer}
              alt="Accréditation RICS Registered Valuer"
              className="svc-rics-badge"
            />
            <p className="svc-lead">
              L'expertise immobilière constitue un acte d'analyse professionnelle
              structuré, destiné à produire une appréciation fondée de la valeur
              et des caractéristiques d'un actif, dans un cadre où les enjeux sont
              à la fois patrimoniaux, financiers, juridiques et décisionnels.
            </p>

            <p>
              Chez <strong>ATAA REALTY</strong>, cette mission est abordée comme
              un processus méthodologique, reposant sur la qualification précise
              de l'objet évalué, la compréhension de son environnement
              réglementaire et économique, et l'application des méthodes
              d'évaluation appropriées à la nature du bien et à la finalité de la
              mission.
            </p>

            <p>
              Nos expertises sont conduites en alignement avec les standards
              internationaux de l'évaluation immobilière, notamment les
              référentiels de la <strong>RICS</strong> et les{' '}
              <strong>International Valuation Standards (IVS)</strong>,
              compatibles avec les exigences de présentation financière (IFRS),
              afin de garantir la pertinence, la cohérence et la robustesse des
              conclusions.
            </p>
          </div>

          {/* COMPOSANTES D'UNE EXPERTISE CONFORME */}
          <div className="svc-section svc-anim-up svc-delay-2">
            <h2 className="svc-section-title">
              Composantes d'une expertise conforme
            </h2>
            <p className="svc-section-intro">
              Une expertise en évaluation immobilière conforme devrait comporter :
            </p>
            <ul className="svc-checklist">
              <li>
                <FaCheck />
                <span>
                  <strong>Analyse juridique, foncière et urbanistique</strong> :
                  propriété immobilière, droits réels, servitudes, inscriptions,
                  situation cadastrale, situation urbanistique (autorisations,
                  cahiers des charges, documents d'urbanisme).
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Analyse technique et physique</strong> : formes
                  géométriques et relief, raccordement, consistance des
                  constructions, état, conformité apparente, potentiel d'usage et
                  d'évolution.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Analyse économique et de marché</strong> :
                  positionnement de l'actif, dynamique de l'offre et de la
                  demande, références de marché pertinentes, étude du marché
                  local.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Mise en œuvre des méthodes d'évaluation adaptées</strong>{' '}
                  : approches par le marché, par le revenu et par le coût, selon
                  le type d'actif et l'objectif poursuivi.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Formalisation de conclusions</strong> motivées,
                  structurées et traçables sur le plan méthodologique.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Présentation de l'ensemble des analyses</strong> dans
                  un rapport d'expertise cohérent : cartographies, descriptifs,
                  reportages photos, analyse SWOT, points de diligence,
                  attestation de valeur, annexes.
                </span>
              </li>
            </ul>
          </div>

          {/* CONTEXTES D'USAGE */}
          <div className="svc-section svc-anim-up svc-delay-2">
            <h2 className="svc-section-title">Contextes d'usage</h2>
            <p className="svc-section-intro">
              Les rapports établis sont destinés à éclairer des situations telles
              que :
            </p>
            <ul className="svc-checklist">
              <li>
                <FaCheck />
                <span>Opérations de financement et de refinancement.</span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Décisions d'investissement, d'arbitrage ou de restructuration
                  d'actifs.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Opérations de transmission, de réorganisation patrimoniale ou
                  de gouvernance.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>Obligations de reporting financier ou institutionnel.</span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Cadres précontentieux ou d'expertise indépendante.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Opérations de transaction immobilière : ventes, acquisitions,
                  mises en location, renégociations contractuelles.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Évaluations à visée informative ou amiable, dans un cadre non
                  contentieux.
                </span>
              </li>
            </ul>
          </div>

          {/* À RETENIR — 3 highlight cards */}
          <div className="svc-section svc-anim-up svc-delay-3">
            <h2 className="svc-section-title">À retenir</h2>
            <p className="svc-highlights-intro">
              Pour les utilisateurs finaux des expertises, les repères suivants
              devraient structurer la conduite de chaque mission :
            </p>
            <div className="svc-highlight-cards">
              <div className="svc-highlight-card">
                <div className="svc-highlight-num">01</div>
                <h4>Cohérence</h4>
                <p>Cohérence des hypothèses retenues tout au long de l'analyse.</p>
              </div>
              <div className="svc-highlight-card">
                <div className="svc-highlight-num">02</div>
                <h4>Traçabilité</h4>
                <p>Traçabilité des sources, données et choix méthodologiques.</p>
              </div>
              <div className="svc-highlight-card">
                <div className="svc-highlight-num">03</div>
                <h4>Lisibilité</h4>
                <p>
                  Lisibilité des résultats restitués aux décideurs et parties
                  prenantes.
                </p>
              </div>
            </div>
            <p>
              Ces principes constituent le cadre de référence de nos travaux,
              afin d'assurer des analyses méthodologiquement fondées,
              vérifiables et adaptées à leurs usages décisionnels, financiers
              et institutionnels.
            </p>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="svc-cta-section">
        <h3>Discutons de votre projet</h3>
        <p>
          Vous avez un actif à évaluer ou un dossier à structurer ? Nos équipes
          vous accompagnent dans une démarche d'expertise rigoureuse, alignée aux
          standards RICS et IVS.
        </p>
        <Link to="/contact" className="svc-cta-btn">
          Nous Contacter <FaArrowRight />
        </Link>
      </section>

      <ServiceCrossNav currentSlug="expertise" />

      <Footer />
    </>
  );
};

export default Expertise;
